import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
} from 'react-native';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '@/components/shared/redesign';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface StepIssueProps {
  userName: string;
  requestType: 'maintenance' | 'it';
  onRequestTypeChange: (type: 'maintenance' | 'it') => void;
  subCategory: string;
  onSubCategoryChange: (value: string) => void;
  subCategoryOptions: string[];
}

/**
 * StepIssue — redesigned Step 1: Category & Subcategory selection.
 * Replaces StepCategorySelection with the redesign theme (GlassCard, useThemeColors).
 * Same props and behavior as the original.
 */
export const StepIssue: React.FC<StepIssueProps> = ({
  userName,
  requestType,
  onRequestTypeChange,
  subCategory,
  onSubCategoryChange,
  subCategoryOptions,
}) => {
  const { colors, semantic } = useThemeColors();
  const [showSubcategoryPicker, setShowSubcategoryPicker] = useState(false);

  const isMaintenanceSelected = requestType === 'maintenance';
  const isITSelected = requestType === 'it';
  const showSubcategory = isMaintenanceSelected;

  const handleMaintenanceSelect = () => {
    onRequestTypeChange('maintenance');
  };

  const handleITSelect = () => {
    onRequestTypeChange('it');
    onSubCategoryChange(''); // Clear subcategory when switching to IT
  };

  const handleSubcategorySelect = (value: string) => {
    onSubCategoryChange(value);
    setShowSubcategoryPicker(false);
  };

  return (
    <View style={styles.container}>
      {/* Page header */}
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.text.primary }]}>
          What do you need help with?
        </Text>
        <Text style={[styles.subtitle, { color: colors.text.dim }]}>
          Hi {userName}, select the category that best describes your request.
        </Text>
      </View>

      <View style={styles.content}>
        {/* Maintenance Category */}
        <GlassCard
          interactive
          onPress={handleMaintenanceSelect}
          padding={REDESIGN_SPACING.cardPadding}
          borderRadius={REDESIGN_SPACING.cardBorderRadius}
          style={
            isMaintenanceSelected
              ? { borderColor: semantic.interactive.primary }
              : undefined
          }
        >
          <View style={styles.categoryRow}>
            {/* Icon */}
            <View style={[styles.iconContainer, { backgroundColor: colors.glass.buttonBg }]}>
              <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH
                width={20}
                height={20}
                color={colors.text.primary}
              />
            </View>

            {/* Info */}
            <View style={styles.categoryInfo}>
              <Text style={[REDESIGN_TYPOGRAPHY.cardTitleLarge, { color: colors.text.primary }]}>
                Maintenance
              </Text>
              <Text style={[REDESIGN_TYPOGRAPHY.caption, { color: colors.text.dim }]}>
                Report issues related to repairs or upkeep
              </Text>
            </View>

            {/* Radio dot */}
            <RadioDot selected={isMaintenanceSelected} />
          </View>

          {/* Subcategory selector — only when maintenance is selected */}
          {showSubcategory && (
            <TouchableOpacity
              style={[
                styles.selectContainer,
                {
                  backgroundColor: colors.glass.buttonBg,
                  borderBottomColor: colors.text.primary,
                },
              ]}
              onPress={() => setShowSubcategoryPicker(true)}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  REDESIGN_TYPOGRAPHY.body,
                  styles.selectText,
                  { color: subCategory ? colors.text.primary : colors.text.dim },
                ]}
              >
                {subCategory || 'Subcategory'}
              </Text>
              <DropdownChevron color={colors.text.dim} />
            </TouchableOpacity>
          )}
        </GlassCard>

        {/* IT Category */}
        <GlassCard
          interactive
          onPress={handleITSelect}
          padding={REDESIGN_SPACING.cardPadding}
          borderRadius={REDESIGN_SPACING.cardBorderRadius}
          style={
            isITSelected
              ? { borderColor: semantic.interactive.primary }
              : undefined
          }
        >
          <View style={styles.categoryRow}>
            {/* Icon */}
            <View style={[styles.iconContainer, { backgroundColor: colors.glass.buttonBg }]}>
              <RESIDENTS_SVG_COMPONENTS.WIFI
                width={20}
                height={20}
                color={colors.text.primary}
              />
            </View>

            {/* Info */}
            <View style={styles.categoryInfo}>
              <Text style={[REDESIGN_TYPOGRAPHY.cardTitleLarge, { color: colors.text.primary }]}>
                IT
              </Text>
              <Text style={[REDESIGN_TYPOGRAPHY.caption, { color: colors.text.dim }]}>
                Report issues with the app or system
              </Text>
            </View>

            {/* Radio dot */}
            <RadioDot selected={isITSelected} />
          </View>
        </GlassCard>
      </View>

      {/* Subcategory Picker Modal */}
      <Modal
        visible={showSubcategoryPicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSubcategoryPicker(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowSubcategoryPicker(false)}
        >
          <View style={[styles.modalContent, { backgroundColor: colors.bg.modal }]}>
            <View style={[styles.modalHeader, { borderBottomColor: colors.separator }]}>
              <Text style={[REDESIGN_TYPOGRAPHY.sectionTitle, { color: colors.text.primary }]}>
                Select Subcategory
              </Text>
              <TouchableOpacity onPress={() => setShowSubcategoryPicker(false)}>
                <Text style={[REDESIGN_TYPOGRAPHY.body, { color: semantic.interactive.primary }]}>
                  Cancel
                </Text>
              </TouchableOpacity>
            </View>

            {subCategoryOptions.length > 0 ? (
              <FlatList
                data={subCategoryOptions}
                keyExtractor={(item, index) => `${item}-${index}`}
                renderItem={({ item }) => (
                  <ModalOption
                    item={item}
                    isSelected={item === subCategory}
                    onPress={handleSubcategorySelect}
                  />
                )}
              />
            ) : (
              <View style={styles.emptyState}>
                <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.dim }]}>
                  No subcategories available
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// --- Sub-components ---

interface RadioDotProps {
  selected: boolean;
}

const RadioDot: React.FC<RadioDotProps> = ({ selected }) => {
  const { colors, semantic } = useThemeColors();
  const dotStyle = [
    styles.radioDot,
    {
      borderColor: colors.card.borderSolid,
      backgroundColor: selected ? semantic.interactive.primary : 'transparent',
    },
  ];
  return <View style={dotStyle} />;
};

interface ModalOptionProps {
  item: string;
  isSelected: boolean;
  onPress: (value: string) => void;
}

const ModalOption: React.FC<ModalOptionProps> = ({ item, isSelected, onPress }) => {
  const { colors, semantic } = useThemeColors();
  return (
    <TouchableOpacity
      style={[styles.modalOption, { borderBottomColor: colors.separator }]}
      onPress={() => onPress(item)}
      activeOpacity={0.7}
    >
      <Text
        style={[
          REDESIGN_TYPOGRAPHY.body,
          { color: isSelected ? semantic.interactive.primary : colors.text.primary },
          isSelected && { fontWeight: '500' },
        ]}
      >
        {item}
      </Text>
      {isSelected && (
        <View style={[styles.checkmark, { borderColor: semantic.interactive.primary }]} />
      )}
    </TouchableOpacity>
  );
};

interface DropdownChevronProps {
  color: string;
}

const DropdownChevron: React.FC<DropdownChevronProps> = ({ color }) => (
  <View style={styles.dropdownIcon}>
    <View style={[styles.dropdownArrow, { borderTopColor: color }]} />
  </View>
);

// --- Styles ---

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: REDESIGN_SPACING.contentGap,
  },
  header: {
    gap: 8,
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    paddingTop: REDESIGN_SPACING.contentGap,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.heading1,
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  content: {
    gap: REDESIGN_SPACING.sectionGap,
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryInfo: {
    flex: 1,
    gap: 4,
  },
  radioDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1.5,
    marginTop: 2,
  },

  // Subcategory dropdown
  selectContainer: {
    marginTop: REDESIGN_SPACING.contentGap,
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
  },
  selectText: {
    flex: 1,
  },

  // Dropdown icon
  dropdownIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropdownArrow: {
    width: 0,
    height: 0,
    borderLeftWidth: 5,
    borderRightWidth: 5,
    borderTopWidth: 5,
    borderStyle: 'solid',
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  modalOption: {
    padding: 16,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
  },
  checkmark: {
    width: 8,
    height: 14,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    transform: [{ rotate: '45deg' }],
  },
});

export default StepIssue;
